import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmt } from "./duct-helpers";
import {
  REFRIGERANTS, satTempFromPressure, pressureFromTemp,
  R410A_LINE_SIZES, CHARGE_PER_FOOT, FACTORY_CHARGE_OZ_PER_TON,
} from "./refrigerant-data";

const ResultCard = ({ label, value, unit, accent }: { label: string; value: string; unit: string; accent?: boolean }) => (
  <div className="trade-card flex justify-between items-center">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className={`font-mono-data text-lg font-bold ${accent ? "text-primary" : "text-foreground"}`}>
      {value} {unit && <span className="text-xs text-muted-foreground font-normal">{unit}</span>}
    </span>
  </div>
);

/* ═══════════════════════════════════════════════════════
   SUPERHEAT CALCULATOR
   ═══════════════════════════════════════════════════════ */
export const SuperheatCalculator = () => {
  const [refIdx, setRefIdx] = useState("0");
  const [suctionPressure, setSuctionPressure] = useState("");
  const [suctionTemp, setSuctionTemp] = useState("");

  const ref = REFRIGERANTS[parseInt(refIdx)];
  const pressure = parseFloat(suctionPressure);
  const actualTemp = parseFloat(suctionTemp);

  const satTemp = useMemo(() => {
    if (!(pressure >= 0)) return null;
    return satTempFromPressure(ref, pressure);
  }, [ref, pressure]);

  const superheat = satTemp !== null && !isNaN(actualTemp) ? actualTemp - satTemp : null;

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          <span className="text-foreground font-semibold">Superheat</span> = Actual suction line temp − Saturated suction temp. Normal range: <span className="text-primary font-semibold">5–15°F</span> for TXV, <span className="text-primary font-semibold">8–12°F</span> target.
        </p>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Refrigerant</label>
          <Select value={refIdx} onValueChange={setRefIdx}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              {REFRIGERANTS.map((r, i) => (
                <SelectItem key={i} value={String(i)}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Suction Pressure (PSIG)</label>
            <Input type="number" inputMode="decimal" placeholder="PSIG…" value={suctionPressure} onChange={(e) => setSuctionPressure(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Suction Line Temp (°F)</label>
            <Input type="number" inputMode="decimal" placeholder="°F…" value={suctionTemp} onChange={(e) => setSuctionTemp(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
        </div>
      </div>
      {satTemp !== null && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Saturated Suction Temp" value={fmt(satTemp, 1)} unit="°F" />
          {superheat !== null && (
            <>
              <ResultCard label="Superheat" value={fmt(superheat, 1)} unit="°F" accent />
              {superheat < 5 && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3">
                  <p className="text-xs text-destructive font-semibold">⚠️ Low superheat — risk of liquid slugging the compressor.</p>
                </div>
              )}
              {superheat > 20 && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3">
                  <p className="text-xs text-destructive font-semibold">⚠️ High superheat — low charge or restricted metering device.</p>
                </div>
              )}
              {superheat >= 5 && superheat <= 15 && (
                <div className="bg-primary/10 border border-primary/30 rounded-xl p-3">
                  <p className="text-xs text-primary font-semibold">✓ Superheat is within normal TXV range (5–15°F).</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   SUBCOOLING CALCULATOR
   ═══════════════════════════════════════════════════════ */
export const SubcoolingCalculator = () => {
  const [refIdx, setRefIdx] = useState("0");
  const [dischargePressure, setDischargePressure] = useState("");
  const [liquidTemp, setLiquidTemp] = useState("");

  const ref = REFRIGERANTS[parseInt(refIdx)];
  const pressure = parseFloat(dischargePressure);
  const actualTemp = parseFloat(liquidTemp);

  const satTemp = useMemo(() => {
    if (!(pressure >= 0)) return null;
    return satTempFromPressure(ref, pressure);
  }, [ref, pressure]);

  const subcooling = satTemp !== null && !isNaN(actualTemp) ? satTemp - actualTemp : null;

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          <span className="text-foreground font-semibold">Subcooling</span> = Saturated condensing temp − Actual liquid line temp. Normal range: <span className="text-primary font-semibold">8–14°F</span> for TXV systems.
        </p>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Refrigerant</label>
          <Select value={refIdx} onValueChange={setRefIdx}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              {REFRIGERANTS.map((r, i) => (
                <SelectItem key={i} value={String(i)}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Discharge Pressure (PSIG)</label>
            <Input type="number" inputMode="decimal" placeholder="PSIG…" value={dischargePressure} onChange={(e) => setDischargePressure(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Liquid Line Temp (°F)</label>
            <Input type="number" inputMode="decimal" placeholder="°F…" value={liquidTemp} onChange={(e) => setLiquidTemp(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
        </div>
      </div>
      {satTemp !== null && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Saturated Condensing Temp" value={fmt(satTemp, 1)} unit="°F" />
          {subcooling !== null && (
            <>
              <ResultCard label="Subcooling" value={fmt(subcooling, 1)} unit="°F" accent />
              {subcooling < 5 && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3">
                  <p className="text-xs text-destructive font-semibold">⚠️ Low subcooling — possible undercharge or condenser issue.</p>
                </div>
              )}
              {subcooling > 20 && (
                <div className="bg-safety/10 border border-safety/30 rounded-xl p-3">
                  <p className="text-xs text-safety font-semibold">⚡ High subcooling — possible overcharge or liquid line restriction.</p>
                </div>
              )}
              {subcooling >= 8 && subcooling <= 14 && (
                <div className="bg-primary/10 border border-primary/30 rounded-xl p-3">
                  <p className="text-xs text-primary font-semibold">✓ Subcooling is within normal TXV range (8–14°F).</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   REFRIGERANT LINE SIZING CALCULATOR
   ═══════════════════════════════════════════════════════ */
export const LineSizingCalculator = () => {
  const [tons, setTons] = useState("");

  const tonsVal = parseFloat(tons);

  const result = useMemo(() => {
    if (!(tonsVal > 0)) return null;
    // Find the first entry that can handle the capacity
    for (const entry of R410A_LINE_SIZES) {
      if (entry.tons >= tonsVal) return entry;
    }
    return R410A_LINE_SIZES[R410A_LINE_SIZES.length - 1];
  }, [tonsVal]);

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Find <span className="text-foreground font-semibold">R-410A copper line sizes</span> for suction, liquid, and hot gas lines based on system tonnage. For equivalent lengths over 50 ft, consider upsizing suction lines.
        </p>
      </div>
      <div>
        <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">System Capacity (Tons)</label>
        <Input type="number" inputMode="decimal" placeholder="Enter tonnage…" value={tons} onChange={(e) => setTons(e.target.value)} className="font-mono-data bg-card border-border" />
      </div>
      {result && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Suction Line" value={result.suction} unit="OD" accent />
          <ResultCard label="Liquid Line" value={result.liquid} unit="OD" />
          <ResultCard label="Hot Gas (Discharge)" value={result.hotGas} unit="OD" />
          <ResultCard label="Rated Capacity" value={String(result.tons)} unit="tons" />
          {tonsVal > 50 && (
            <div className="bg-safety/10 border border-safety/30 rounded-xl p-3">
              <p className="text-xs text-safety font-semibold">⚡ Above 50 tons — verify with manufacturer data for exact sizing.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   CHARGE ESTIMATOR
   ═══════════════════════════════════════════════════════ */
export const ChargeEstimator = () => {
  const [tons, setTons] = useState("");
  const [linesetLength, setLinesetLength] = useState("");
  const [factoryLineset, setFactoryLineset] = useState("25");
  const [liquidLineSize, setLiquidLineSize] = useState("0");

  const tonsVal = parseFloat(tons);
  const totalLength = parseFloat(linesetLength);
  const factoryLength = parseFloat(factoryLineset);
  const chargeEntry = CHARGE_PER_FOOT[parseInt(liquidLineSize)];

  const hasInput = tonsVal > 0 && totalLength > 0 && factoryLength >= 0;
  const additionalLength = Math.max(0, totalLength - factoryLength);
  const additionalChargeOz = hasInput ? additionalLength * chargeEntry.ozPerFt : 0;
  const factoryChargeOz = tonsVal * FACTORY_CHARGE_OZ_PER_TON;
  const totalChargeOz = factoryChargeOz + additionalChargeOz;
  const totalChargeLb = totalChargeOz / 16;

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Estimate total <span className="text-foreground font-semibold">refrigerant charge</span> based on factory charge + additional lineset length. Always verify with OEM specifications.
        </p>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">System (Tons)</label>
            <Input type="number" inputMode="decimal" placeholder="Tons…" value={tons} onChange={(e) => setTons(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Liquid Line Size</label>
            <Select value={liquidLineSize} onValueChange={setLiquidLineSize}>
              <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CHARGE_PER_FOOT.map((c, i) => (
                  <SelectItem key={i} value={String(i)}>{c.size} ({c.ozPerFt} oz/ft)</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Total Lineset (ft)</label>
            <Input type="number" inputMode="decimal" placeholder="Total feet…" value={linesetLength} onChange={(e) => setLinesetLength(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Factory Lineset (ft)</label>
            <Input type="number" inputMode="decimal" placeholder="25…" value={factoryLineset} onChange={(e) => setFactoryLineset(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
        </div>
      </div>
      {hasInput && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Total Estimated Charge" value={fmt(totalChargeOz, 1)} unit="oz" accent />
          <ResultCard label="Total Charge" value={fmt(totalChargeLb, 2)} unit="lb" accent />
          <div className="border-t border-border my-2" />
          <ResultCard label="Factory Charge (est.)" value={fmt(factoryChargeOz, 1)} unit="oz" />
          <ResultCard label="Additional Length" value={fmt(additionalLength)} unit="ft" />
          <ResultCard label="Additional Charge" value={fmt(additionalChargeOz, 1)} unit="oz" />
          <ResultCard label="Charge Rate" value={fmt(chargeEntry.ozPerFt, 2)} unit="oz/ft" />
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   TEMP-PRESSURE LOOKUP (reverse: enter temp → get pressure)
   ═══════════════════════════════════════════════════════ */
export const TempPressureLookup = () => {
  const [refIdx, setRefIdx] = useState("0");
  const [temp, setTemp] = useState("");

  const ref = REFRIGERANTS[parseInt(refIdx)];
  const tempVal = parseFloat(temp);

  const pressure = useMemo(() => {
    if (isNaN(tempVal)) return null;
    return pressureFromTemp(ref, tempVal);
  }, [ref, tempVal]);

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Quick <span className="text-foreground font-semibold">temperature → pressure</span> lookup for common refrigerants. Enter a saturated temperature to find the corresponding gauge pressure.
        </p>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Refrigerant</label>
          <Select value={refIdx} onValueChange={setRefIdx}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              {REFRIGERANTS.map((r, i) => (
                <SelectItem key={i} value={String(i)}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Saturated Temperature (°F)</label>
          <Input type="number" inputMode="decimal" placeholder="°F…" value={temp} onChange={(e) => setTemp(e.target.value)} className="font-mono-data bg-card border-border" />
        </div>
      </div>
      {pressure !== null && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label={`${ref.label} Pressure`} value={fmt(pressure, 1)} unit="PSIG" accent />
          <ResultCard label="Temperature" value={fmt(tempVal, 1)} unit="°F" />
        </div>
      )}
    </div>
  );
};
